import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Typography } from "@mui/material";

// ─── Timing (ms) — everything breathes slowly ────────────────────────────────
const T = {
  U_IN: 0,
  L_IN: 400,
  CAP_IN: 900,
  SCROLL_IN: 1200,
  WORD_IN: 1550,
  SHIMMER: 2000,
  HOLD: 3800,
  EXIT: 4200,
  DONE: 5000,
};

const EASE = [0.16, 1, 0.3, 1];
const EASE_SP = [0.34, 1.3, 0.64, 1]; // gentle overshoot

export default function PageLoader({ onDone }) {
  const [phase, setPhase] = useState("enter");
  const [uVisible, setU] = useState(false);
  const [lVisible, setL] = useState(false);
  const [capVisible, setCap] = useState(false);
  const [scrollVis, setScroll] = useState(false);
  const [wordVis, setWord] = useState(false);
  const [shimmer, setShimmer] = useState(false);
  const timers = useRef([]);
  const t = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
  };

  useEffect(() => {
    t(() => setU(true), T.U_IN);
    t(() => setL(true), T.L_IN);
    t(() => setCap(true), T.CAP_IN);
    t(() => setScroll(true), T.SCROLL_IN);
    t(() => setWord(true), T.WORD_IN);
    t(() => setShimmer(true), T.SHIMMER);
    t(() => setPhase("exit"), T.EXIT);
    t(() => onDone?.(), T.DONE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => timers.current.forEach(clearTimeout);
  }, [onDone]);

  return (
    <AnimatePresence>
      {phase === "enter" && (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.8, ease: EASE } }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            // ── Brighter: deep navy-blue instead of near-black ──────────
            background:
              "linear-gradient(160deg, #0f2744 0%, #162d4a 50%, #0f2340 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* ── Ambient orbs — bigger + brighter ─────────────────────── */}
          <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            <Box
              sx={{
                position: "absolute",
                width: 600,
                height: 600,
                borderRadius: "50%",
                top: -150,
                left: -120,
                background: "rgba(236,72,153,0.09)",
                filter: "blur(100px)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: 500,
                height: 500,
                borderRadius: "50%",
                bottom: -100,
                right: -100,
                background: "rgba(2,136,209,0.10)",
                filter: "blur(90px)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: 350,
                height: 350,
                borderRadius: "50%",
                top: "35%",
                left: "52%",
                background: "rgba(236,72,153,0.06)",
                filter: "blur(80px)",
              }}
            />
            {/* Centre lift — brightens the middle where the logo sits */}
            <Box
              sx={{
                position: "absolute",
                width: 400,
                height: 300,
                borderRadius: "50%",
                top: "30%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "rgba(255,255,255,0.028)",
                filter: "blur(60px)",
              }}
            />
          </Box>

          {/* ── Dot grid ─────────────────────────────────────────────── */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.07,
              pointerEvents: "none",
              backgroundImage:
                "radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />

          {/* ══════════════════════════════════════════════════════════
              LOGO SVG
          ══════════════════════════════════════════════════════════ */}
          <svg
            viewBox="0 0 220 220"
            width="210"
            height="210"
            style={{ overflow: "visible", marginBottom: 28 }}
          >
            <defs>
              {/* ── U gradient — rich pink ────────────────────────── */}
              <linearGradient id="uGrad" x1="0%" y1="0%" x2="10%" y2="100%">
                <stop offset="0%" stopColor="#f9a8d4" />
                <stop offset="45%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#be185d" />
              </linearGradient>

              {/* ── U inner highlight ─────────────────────────────── */}
              <linearGradient
                id="uHighlight"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>

              {/* ── L gradient — slate blue (visible on navy bg) ──── */}
              <linearGradient id="lGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#e2e8f0" />
                <stop offset="40%" stopColor="#cbd5e1" />
                <stop offset="100%" stopColor="#94a3b8" />
              </linearGradient>

              {/* ── L edge highlight ──────────────────────────────── */}
              <linearGradient id="lEdge" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
              </linearGradient>

              {/* ── Gold ─────────────────────────────────────────── */}
              <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fde68a" />
                <stop offset="50%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#b45309" />
              </linearGradient>

              {/* ── Shimmer sweep ─────────────────────────────────── */}
              <linearGradient
                id="shimmerGrad"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
                gradientUnits="userSpaceOnUse"
                // x2="200"
                // y2="0"
              >
                <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                <stop offset="40%" stopColor="rgba(255,255,255,0.15)" />
                <stop offset="50%" stopColor="rgba(255,255,255,0.28)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>

              {/* ── Soft glow on U ────────────────────────────────── */}
              <filter id="uGlow" x="-25%" y="-25%" width="150%" height="150%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              {/* ── Crisp shadow on L ─────────────────────────────── */}
              <filter id="lShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow
                  dx="2"
                  dy="3"
                  stdDeviation="4"
                  floodColor="rgba(0,0,0,0.4)"
                />
              </filter>

              {/* ── Cap shadow ────────────────────────────────────── */}
              <filter
                id="capShadow"
                x="-30%"
                y="-30%"
                width="160%"
                height="160%"
              >
                <feDropShadow
                  dx="0"
                  dy="5"
                  stdDeviation="5"
                  floodColor="rgba(0,0,0,0.45)"
                />
              </filter>

              {/* ── Scroll paper shadow ───────────────────────────── */}
              <filter
                id="scrollShadow"
                x="-20%"
                y="-20%"
                width="140%"
                height="140%"
              >
                <feDropShadow
                  dx="1"
                  dy="3"
                  stdDeviation="3"
                  floodColor="rgba(0,0,0,0.3)"
                />
              </filter>
            </defs>

            {/* ══════════════════════════════════════════════════════
                U  —  rises from below, pink serif-style letterform
            ══════════════════════════════════════════════════════ */}
            <motion.g
              initial={{ opacity: 0, y: 30, scale: 0.92 }}
              animate={uVisible ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 1.1, ease: EASE_SP }}
              style={{ transformOrigin: "110px 140px" }}
            >
              {/* Main U body — thick premium letterform with serifs */}
              <path
                d={`
                  M 50 72
                  L 50 148 Q 50 185 110 185 Q 170 185 170 148
                  L 170 72 L 148 72 L 148 148
                  Q 148 163 110 163 Q 72 163 72 148
                  L 72 72 Z
                `}
                fill="url(#uGrad)"
                filter="url(#uGlow)"
              />
              {/* Serif feet on left arm */}
              <rect
                x="44"
                y="68"
                width="34"
                height="10"
                rx="2"
                fill="url(#uGrad)"
                filter="url(#uGlow)"
              />
              {/* Serif feet on right arm */}
              <rect
                x="142"
                y="68"
                width="34"
                height="10"
                rx="2"
                fill="url(#uGrad)"
                filter="url(#uGlow)"
              />
              {/* Inner highlight bevel */}
              <path
                d={`
                  M 50 72 L 72 72 L 72 148
                  Q 72 163 110 163 Q 148 163 148 148
                  L 148 72 L 170 72 L 170 75
                  L 151 75 L 151 148 Q 151 160 110 160
                  Q 69 160 69 148 L 69 75 L 50 75 Z
                `}
                fill="url(#uHighlight)"
              />
              {/* Shimmer sweep */}
              {shimmer && (
                <motion.path
                  d={`M 50 72 L 50 148 Q 50 185 110 185 Q 170 185 170 148 L 170 72 L 148 72 L 148 148 Q 148 163 110 163 Q 72 163 72 148 L 72 72 Z`}
                  fill="url(#shimmerGrad)"
                  initial={{ opacity: 0, x: -60 }}
                  animate={{ opacity: [0, 1, 1, 0], x: [-60, 0, 60, 120] }}
                  transition={{ duration: 1.0, ease: "easeInOut" }}
                />
              )}
            </motion.g>

            {/* ══════════════════════════════════════════════════════
                L  —  drops from above, silver-slate letterform
            ══════════════════════════════════════════════════════ */}
            <motion.g
              initial={{ opacity: 0, y: -30, scale: 0.93 }}
              animate={lVisible ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 1.1, ease: EASE_SP }}
              style={{ transformOrigin: "100px 125px" }}
            >
              {/* Vertical stem */}
              <rect
                x="88"
                y="28"
                width="32"
                height="158"
                rx="4"
                fill="url(#lGrad)"
                filter="url(#lShadow)"
              />
              {/* Horizontal foot */}
              <rect
                x="88"
                y="159"
                width="76"
                height="27"
                rx="4"
                fill="url(#lGrad)"
                filter="url(#lShadow)"
              />
              {/* Serif cap on top of stem */}
              <rect
                x="80"
                y="28"
                width="48"
                height="11"
                rx="3"
                fill="url(#lGrad)"
                filter="url(#lShadow)"
              />
              {/* Edge highlight on stem (left edge) */}
              <rect
                x="88"
                y="28"
                width="6"
                height="158"
                rx="3"
                fill="url(#lEdge)"
              />
              {/* Edge highlight on foot (top edge) */}
              <rect
                x="88"
                y="159"
                width="76"
                height="5"
                rx="2"
                fill="url(#lEdge)"
              />
              {/* Bottom-right serif corner */}
              <rect
                x="152"
                y="159"
                width="12"
                height="27"
                rx="3"
                fill="url(#lGrad)"
                filter="url(#lShadow)"
              />
            </motion.g>

            {/* ══════════════════════════════════════════════════════
                GRADUATION CAP — floats down with settle
            ══════════════════════════════════════════════════════ */}
            <motion.g
              initial={{ opacity: 0, y: -36, rotate: -10 }}
              animate={capVisible ? { opacity: 1, y: 0, rotate: 0 } : {}}
              transition={{ duration: 1.15, ease: EASE_SP }}
              style={{ transformOrigin: "108px 30px" }}
            >
              {/* Board */}
              <rect
                x="62"
                y="22"
                width="90"
                height="14"
                rx="3"
                fill="#1a1a2e"
                filter="url(#capShadow)"
              />
              {/* Diamond top */}
              <polygon points="107,4 128,22 107,32 86,22" fill="#12122a" />
              {/* Gold band */}
              <rect
                x="62"
                y="27"
                width="90"
                height="5"
                rx="1"
                fill="url(#goldGrad)"
                opacity="0.9"
              />
              {/* Cap button */}
              <circle cx="107" cy="6" r="4.5" fill="#1f1f3a" />
              <circle cx="107" cy="6" r="2.2" fill="url(#goldGrad)" />

              {/* Tassel cord */}
              <path
                d="M 86 6 Q 76 18 74 34"
                stroke="url(#goldGrad)"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
              {/* Tassel body */}
              <rect
                x="70"
                y="34"
                width="9"
                height="20"
                rx="4"
                fill="url(#goldGrad)"
              />
              {/* Tassel fringe threads */}
              {[0, 2, 4, 6, 8].map((x, i) => (
                <line
                  key={i}
                  x1={70.5 + x}
                  y1="54"
                  x2={70 + x + (i % 2 === 0 ? -1 : 1)}
                  y2={65 + (i % 3) * 3}
                  stroke="#fbbf24"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  opacity="0.95"
                />
              ))}
            </motion.g>

            {/* ══════════════════════════════════════════════════════
                DIPLOMA SCROLL — swoops in from right
            ══════════════════════════════════════════════════════ */}
            <motion.g
              initial={{ opacity: 0, x: 28, rotate: 20 }}
              animate={scrollVis ? { opacity: 1, x: 0, rotate: 9 } : {}}
              transition={{ duration: 1.0, ease: EASE_SP }}
              style={{ transformOrigin: "152px 44px" }}
            >
              {/* Paper body */}
              <rect
                x="130"
                y="16"
                width="42"
                height="52"
                rx="5"
                fill="#f8fafc"
                filter="url(#scrollShadow)"
              />
              {/* Top curl shadow */}
              <ellipse cx="151" cy="16" rx="21" ry="6" fill="#e2e8f0" />
              {/* Bottom curl shadow */}
              <ellipse cx="151" cy="68" rx="21" ry="6" fill="#e2e8f0" />
              {/* Paper lines */}
              {[28, 36, 44, 52].map((y, i) => (
                <line
                  key={i}
                  x1="136"
                  y1={y}
                  x2={i === 3 ? 160 : 166}
                  y2={y}
                  stroke="#cbd5e1"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              ))}
              {/* Gold ribbon */}
              <rect
                x="144"
                y="12"
                width="14"
                height="58"
                rx="4"
                fill="url(#goldGrad)"
                opacity="0.8"
              />
              {/* Ribbon knot */}
              <ellipse cx="151" cy="42" rx="9" ry="6" fill="url(#goldGrad)" />
              <ellipse
                cx="151"
                cy="42"
                rx="4.5"
                ry="3"
                fill="#fef9c3"
                opacity="0.65"
              />
              {/* Ribbon tails */}
              <path
                d="M 148 48 Q 144 56 142 62"
                stroke="url(#goldGrad)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                opacity="0.8"
              />
              <path
                d="M 154 48 Q 158 56 160 62"
                stroke="url(#goldGrad)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                opacity="0.8"
              />
            </motion.g>
          </svg>

          {/* ══════════════════════════════════════════════════════════
              WORDMARK — letter-by-letter with generous stagger
          ══════════════════════════════════════════════════════════ */}
          <AnimatePresence>
            {wordVis && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0,
                  marginBottom: 10,
                }}
              >
                {"LITE-UPPER".split("").map((char, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 14, filter: "blur(5px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 0.6, ease: EASE, delay: i * 0.06 }}
                    style={{
                      fontWeight: 800,
                      fontSize: char === "-" ? "1.05rem" : "1.5rem",
                      letterSpacing: "5px",
                      color: char === "-" ? "#ec4899" : "#f1f5f9",
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      lineHeight: 1,
                      textShadow:
                        char !== "-"
                          ? "0 2px 12px rgba(255,255,255,0.1)"
                          : "0 2px 8px rgba(236,72,153,0.4)",
                    }}
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tagline */}
          <AnimatePresence>
            {wordVis && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, ease: EASE, delay: 0.7 }}
              >
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    letterSpacing: "3.5px",
                    textTransform: "uppercase",
                    color: "rgba(236,72,153,0.5)",
                  }}
                >
                  Smarter Learning
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Bottom progress bar ───────────────────────────────────── */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 2.5,
            }}
          >
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: T.HOLD / 1000, ease: "linear" }}
              style={{
                height: "100%",
                background:
                  "linear-gradient(90deg, #ec4899, #0288D1, #7c3aed, #ec4899)",
                backgroundSize: "200% 100%",
                transformOrigin: "left center",
              }}
            />
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
